<template>
  <div class="py-3">
    <div class="d-flex align-items-center justify-content-between flex-wrap mb-3" style="gap:10px;">
      <h4 class="text-primary mb-0" style="font-weight:600;">Chart of Accounts</h4>
      <div class="d-flex" style="gap:8px;">
        <button class="btn btn-light" :disabled="generating" @click="generateAccounts()">
          <span v-if="generating"><span class="spinner-border spinner-border-sm mr-1"></span>Generating…</span>
          <span v-else><i class="bi bi-arrow-repeat mr-1"></i>Generate accounts</span>
        </button>
        <button class="btn btn-primary" @click="openAddAccount()">
          <i class="bi bi-plus-circle mr-1"></i>Add a new account
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center text-secondary py-5">
      <span class="spinner-border spinner-border-sm mr-2"></span>Loading…
    </div>

    <template v-else>
      <ul class="nav nav-pills mb-3">
        <li class="nav-item" v-for="tab in tabs" :key="tab.type">
          <a
            href="#"
            class="nav-link"
            :class="activeType === tab.type ? 'active text-white' : ''"
            :style="activeType === tab.type ? { background: '#fd4719' } : {}"
            @click.prevent="activeType = tab.type"
          >
            {{ tab.label }} <span class="badge badge-light ml-1">{{ accountCount(tab.type) }}</span>
          </a>
        </li>
      </ul>

      <div v-for="group in groupsForActiveTab" :key="group.id" class="mb-4">
        <div class="table-responsive bg-white border" style="border-radius:10px;">
          <table class="table mb-0">
            <thead>
              <tr class="table-light">
                <th colspan="4">{{ group.name }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!group.accounts.length">
                <td colspan="3" class="text-secondary">You haven't added any {{ group.name }} account yet.</td>
                <td class="text-right">
                  <a href="#" @click.prevent="openAddAccount(group)">Add account</a>
                </td>
              </tr>
              <tr v-for="account in group.accounts" :key="account.id">
                <td class="text-secondary" style="width:100px;">{{ account.code }}</td>
                <td class="text-dark font-weight-bold">{{ account.name }}</td>
                <td class="text-secondary">{{ account.description }}</td>
                <td class="text-right">{{ formatCurrency(account.calculated_balance) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- Add account modal -->
    <b-modal v-model="showModal" title="Add an account" hide-footer>
      <div v-if="formError" class="alert alert-danger py-2" style="font-size:13px;">{{ formError }}</div>

      <div class="form-group">
        <label>Account type</label>
        <select class="form-control" v-model="form.account_group_id">
          <option value="" disabled>Select account type</option>
          <optgroup v-for="tab in tabs" :key="tab.type" :label="tab.label">
            <option v-for="group in groupsByType(tab.type)" :key="group.id" :value="group.id">{{ group.name }}</option>
          </optgroup>
        </select>
      </div>
      <div class="form-group">
        <label>Account name</label>
        <input type="text" class="form-control" v-model="form.name">
      </div>
      <div class="form-group">
        <label>Account code <span class="text-muted">(optional)</span></label>
        <input type="text" class="form-control" v-model="form.code">
      </div>
      <div class="form-group">
        <label>Opening balance (KES) <span class="text-muted">(optional)</span></label>
        <input type="number" min="0" step="0.01" class="form-control" v-model="form.opening_balance">
      </div>
      <div class="form-group">
        <label>Description</label>
        <textarea class="form-control" rows="2" v-model="form.description"></textarea>
      </div>

      <div class="d-flex justify-content-end mt-3" style="gap:8px;">
        <button class="btn btn-light" @click="showModal = false">Cancel</button>
        <button class="btn btn-primary" :disabled="saving" @click="submit">
          <span v-if="saving"><span class="spinner-border spinner-border-sm mr-1"></span>Saving…</span>
          <span v-else>Add account</span>
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';

const TABS = [
  { type: 'asset', label: 'Assets' },
  { type: 'liability', label: 'Liabilities & Credit Cards' },
  { type: 'revenue', label: 'Income' },
  { type: 'expense', label: 'Expenses' },
  { type: 'equity', label: 'Equity' },
];

export default {
  name: 'ChartOfAccounts',

  data() {
    return {
      loading: true,
      generating: false,
      groups: [],
      activeType: 'asset',
      tabs: TABS,

      showModal: false,
      saving: false,
      formError: '',
      form: { account_group_id: '', name: '', code: '', opening_balance: '', description: '' },
    };
  },

  computed: {
    groupsForActiveTab() {
      return this.groupsByType(this.activeType);
    },
  },

  mounted() {
    this.fetchChartOfAccounts();
  },

  methods: {
    formatCurrency(v) {
      return 'KES ' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    groupsByType(type) {
      return this.groups.filter(g => g.type === type);
    },

    accountCount(type) {
      return this.groupsByType(type).reduce((sum, g) => sum + g.accounts.length, 0);
    },

    async generateAccounts() {
      this.generating = true;
      try {
        await this.fetchChartOfAccounts();
        this.$toasts.push({ type: 'success', message: 'Standard chart of accounts generated.' });
      } finally {
        this.generating = false;
      }
    },

    async fetchChartOfAccounts() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/ledger/chart-of-accounts');
        this.groups = data;
      } catch (_) {
        this.groups = [];
      } finally {
        this.loading = false;
      }
    },

    openAddAccount(group) {
      this.formError = '';
      this.form = { account_group_id: group ? group.id : '', name: '', code: '', opening_balance: '', description: '' };
      this.showModal = true;
    },

    async submit() {
      this.formError = '';
      if (!this.form.account_group_id) {
        this.formError = 'Select an account type.';
        return;
      }
      if (!this.form.name.trim()) {
        this.formError = 'Enter an account name.';
        return;
      }

      this.saving = true;
      try {
        await axios.post('/api/ledger/accounts', this.form);
        this.showModal = false;
        this.$toasts.push({ type: 'success', message: 'Account added.' });
        this.fetchChartOfAccounts();
      } catch (err) {
        this.formError = err.response?.data?.message || 'Could not add this account.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
